import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect } from 'react'
import { Trans } from 'react-i18next'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { AsyncStateN } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { nativeNetworkCurrencyAtom } from '@root/entities/currency'
import { useTranslation } from '@root/shared/localization'
import { Alert, Drawer, Fade } from '@root/shared/ui-kit'

import { reatomEditLimitOrderDrawer } from '../atoms'

import type { LimitOrderModel } from '@root/entities/limit-order/atoms'
import type { DrawerControlProps } from '@root/shared/ui-kit'

type Props = DrawerControlProps & {
  order: LimitOrderModel
}

export const EditOrderDrawer: React.FC<Props> = ({
  open,
  onOpenChange,
  order,
}) => {
  const { t } = useTranslation()

  const { editCostAtom, updateOrderStateAtom } = useMemo(
    () =>
      reatomEditLimitOrderDrawer({
        pairAddress: order.pair.address,
        walletId: order.walletId,
        order,
      }),
    [order],
  )

  const [cost] = useAtom(editCostAtom)
  const [updateState] = useAtom(updateOrderStateAtom)
  const [nativeCurrency] = useAtom(nativeNetworkCurrencyAtom)

  const update = useAction(updateOrderStateAtom.execute)

  useEffect(() => {
    if (AsyncStateN.isSuccess(updateState)) {
      onOpenChange?.(false)
    }
  }, [onOpenChange, updateState])

  return (
    <>
      <Root
        open={open}
        onOpenChange={onOpenChange}
        title={t('order.edit-order')}
      >
        <Content>
          {/* TODO: Wait for designs */}
          <AlertContainer>
            <Fade>
              {cost.gt(0) ? (
                <Alert intent="warning">
                  <Trans
                    i18nKey={'order.edit-fee-alert'}
                    components={{
                      n: (
                        <Numeric
                          value={cost}
                          currency={nativeCurrency.ticker}
                          preset="currency-amount-short"
                        />
                      ),
                    }}
                  />
                </Alert>
              ) : undefined}
            </Fade>
          </AlertContainer>

          <Actions>
            <Button variant="secondary" size="m">
              {t('common.cancel')}
            </Button>
            <Button
              busy={updateState.loading}
              variant="solid"
              size="m"
              color="primary"
              onClick={() => update()}
            >
              {cost.gt(0) ? (
                <Trans
                  i18nKey={'common.change-for-n'}
                  components={{
                    n: (
                      <Numeric
                        value={cost}
                        currency={nativeCurrency.ticker}
                        preset="currency-amount-short"
                      />
                    ),
                  }}
                />
              ) : (
                t('common.change')
              )}
            </Button>
          </Actions>
        </Content>
      </Root>
    </>
  )
}

const Root = styled(Drawer)``

const Content = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;

  height: 100%;
  padding-block: 16px var(--safe-bottom);
  padding-inline: 12px;
`

const Actions = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 6px;
`

const AlertContainer = styled.div`
  height: 82px;
  display: grid;
  align-items: center;
`
